import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  ImageIcon as ImageIconIcon,
  X as XIcon,
  Layers as LayersIcon,
} from 'lucide-react';

// Cast Lucide icons to avoid "ReactNode is not a valid JSX element" errors (project-wide TS config issue)
type LucideProps = { size?: number; className?: string };
const ImageIcon = ImageIconIcon as React.FC<LucideProps>;
const X = XIcon as React.FC<LucideProps>;
const Layers = LayersIcon as React.FC<LucideProps>;
import {
  useGetGeneralViewerInstances,
  clearGeneralViewerFrameCache,
  type GeneralViewerInstance,
} from '../../../helpers/api/view_frame/view-frames';
import GeneralViewerInstanceDetail from './GeneralViewerInstanceDetail';
import { Select } from '../../ComponnentsUI/common/Select';
import GeneralViewerDicomMetadataPanel from './GeneralViewerDicomMetadataPanel';
import { ViewerToolCluster, type LiveViewerExportHandlers } from './ViewerToolCluster';
import { apiClient } from '../../../helpers/apiHelper';
import {
  canonicalizeCategoryGroup,
  useFilterOptions,
} from '../../../contexts/FilterOptionsContext';
import {
  SmartViewerFilter,
  type SmartFilterOption,
  type SmartViewerFilterValue,
} from '../ViewerFilter/SmartViewerFilter';


interface SeriesInfo {
  serie_instance_uid: string;
  type: string;
}

interface GeneralViewerProps {
  studyUID: string;
  availableSeries: SeriesInfo[];
  /** When set, pre-select this view_code for assignment so the user can draw and confirm directly. */
  initialPendingViewCode?: string | null;
  /** Called after the user confirms & saves the pre-selected view. Navigate back to Views tab here. */
  onViewConfirmedAndSaved?: () => void;
  /** When navigating from the exam list thumbnail strip: jump straight to this instance. */
  initialSopInstanceUID?: string | null;
  /** DB primary key (frame_id) of the frame to jump to on open. */
  initialFrameId?: string | null;
}

export default function GeneralViewer({ studyUID, availableSeries, initialPendingViewCode, onViewConfirmedAndSaved, initialSopInstanceUID, initialFrameId }: GeneralViewerProps) {
  const { t } = useTranslation('GeneralViewer');
  const [selectedInstance, setSelectedInstance] = useState<GeneralViewerInstance | null>(null);
  const [selectedSerieType, setSelectedSerieType] = useState<string[]>(['All']);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedViews, setSelectedViews] = useState<string[]>([]);
  const [selectedQualityFlags, setSelectedQualityFlags] = useState<string[]>([]);
  const [failedThumbnailIds, setFailedThumbnailIds] = useState<Set<string>>(new Set());
  const [thumbnailBlobUrls, setThumbnailBlobUrls] = useState<Record<string, string>>({});
  const [filteredFrameCounts, setFilteredFrameCounts] = useState<Record<string, number>>({});
  const [instancePanelOpen, setInstancePanelOpen] = useState(true);
  const [dicomMetadataOpen, setDicomMetadataOpen] = useState(false);
  const [liveExport, setLiveExport] = useState<LiveViewerExportHandlers | null>(null);
  const thumbnailBlobUrlsRef = useRef<Record<string, string>>({});
  const selectedThumbRef = useRef<HTMLButtonElement>(null);

  thumbnailBlobUrlsRef.current = thumbnailBlobUrls;

  const { groups: uniqueGroups, views: configViews, qualityFlags: availableQualityFlags, categoryIdToGroup } = useFilterOptions();

  const viewDisplayNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    configViews.forEach((v: any) => {
      if (v?.view_code) m.set(v.view_code, v.display_name || v.view_code);
    });
    return m;
  }, [configViews]);

  const prettifyViewCode = useCallback((code: string) => {
    const raw = String(code || '');
    const stripped = raw.startsWith('VIEW__') ? raw.slice('VIEW__'.length) : raw;
    return stripped
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();
  }, []);

  const formatGroup = useCallback(
    (g: string) => {
      const key = canonicalizeCategoryGroup(g);
      return t(`group.${key}`, {
        defaultValue: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      });
    },
    [t]
  );

  const formatView = useCallback(
    (code: string) =>
      t(`viewTag.${code}`, {
        defaultValue: viewDisplayNameByCode.get(code) || prettifyViewCode(code),
      }),
    [t, viewDisplayNameByCode, prettifyViewCode]
  );

  const formatQualityFlag = useCallback(
    (opt: { code: string; name: string }) => {
      const raw = String(opt.code ?? '');
      // Backend codes can be like:
      // - "abdomen"
      // - "QUAL_FLAG__abdomen"
      // - "QUAL_FLAG__cavum_septum_pellucidum_segment_polygon"
      // Normalize so i18n can map them to stable keys.
      const normalized = raw
        .replace(/^QUAL_FLAG__/, '')
        .replace(/_(segment_polygon|box_segment|segment_box|segment|polygon|box)$/i, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
      return t(`qualityFlag.${normalized}`, { defaultValue: opt.name });
    },
    [t]
  );

  const availableViews = useMemo(() => {
    const viewCodes = configViews.map((v) => v.view_code);
    if (selectedGroups.length === 0) return viewCodes;
    return configViews
      .filter((v) =>
        v.quality_rules?.some(
          (r) => selectedGroups.includes(categoryIdToGroup[r.category_id])
        )
      )
      .map((v) => v.view_code);
  }, [configViews, selectedGroups, categoryIdToGroup]);

  const smartFilter: SmartViewerFilterValue = useMemo(
    () => ({
      contentScope: 'all',
      views: selectedViews,
      structureIds: selectedQualityFlags,
      groups: selectedGroups,
    }),
    [selectedViews, selectedQualityFlags, selectedGroups]
  );

  const groupOptions: SmartFilterOption[] = useMemo(
    () => uniqueGroups.map((g) => ({ id: g, label: formatGroup(g) })),
    [uniqueGroups, formatGroup]
  );

  const viewOptions: SmartFilterOption[] = useMemo(
    () => availableViews.map((code) => ({ id: code, label: formatView(code) })),
    [availableViews, formatView]
  );

  const structureOptions: SmartFilterOption[] = useMemo(
    () =>
      availableQualityFlags.map((opt) => ({
        // Prefer numeric category id — backend accepts id strings and matches segmentations reliably.
        id: opt.id,
        label: formatQualityFlag(opt),
      })),
    [availableQualityFlags, formatQualityFlag]
  );

  const handleSmartFilterChange = useCallback((next: SmartViewerFilterValue) => {
    if (
      next.contentScope === 'all' &&
      next.views.length === 0 &&
      next.structureIds.length === 0 &&
      !(next.groups?.length)
    ) {
      setSelectedGroups([]);
      setSelectedViews([]);
      setSelectedQualityFlags([]);
      return;
    }
    setSelectedGroups(
      Array.from(
        new Set((next.groups ?? []).map(canonicalizeCategoryGroup).filter(Boolean))
      )
    );
    setSelectedViews(next.views);
    // Send category ids (as strings) so /view-frame filters resolve even when code casing/prefix differs.
    setSelectedQualityFlags(next.structureIds.map(String));
  }, []);

  const seriesIds = useMemo(() => {
    if (selectedSerieType.length === 0 || (selectedSerieType.length === 1 && selectedSerieType[0] === 'All'))
      return undefined;
    return selectedSerieType.filter((s) => s !== 'All');
  }, [selectedSerieType]);

  const filters = useMemo(
    () => ({
      viewCodes: selectedViews.length ? selectedViews : undefined,
      qualityFlagCodes: selectedQualityFlags.length ? selectedQualityFlags : undefined,
      categoryGroups: selectedGroups.length ? selectedGroups : undefined,
    }),
    [selectedViews, selectedQualityFlags, selectedGroups]
  );

  // filteredFrameCounts is kept only as a fine-grained override for the currently
  // open instance (provided by GeneralViewerInstanceDetail via onFrameCountLoaded).
  // All other instances get their filtered count directly from the backend response
  // via inst.filtered_frame_count, so clearing this on filter change is enough.
  const filtersKey = JSON.stringify(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFilteredFrameCounts({}); }, [filtersKey]);

  const {
    data: instancesResponse,
    isLoading: isLoadingInstances,
    isError: isErrorInstances,
  } = useGetGeneralViewerInstances(studyUID, seriesIds, filters);

  const instances: GeneralViewerInstance[] = useMemo(() => {
    const raw = instancesResponse?.data?.data ?? instancesResponse?.data ?? [];
    return Array.isArray(raw) ? raw : [];
  }, [instancesResponse]);

  /** Series UID for DICOMweb study/series metadata (prefer current instance, else first loaded instance, else first configured series). */
  const primarySeriesUid = useMemo(() => {
    if (selectedInstance?.serie_instance_uid) return selectedInstance.serie_instance_uid;
    const first = instances[0];
    if (first?.serie_instance_uid) return first.serie_instance_uid;
    const s = availableSeries[0];
    if (s?.serie_instance_uid) return s.serie_instance_uid;
    return null;
  }, [selectedInstance, instances, availableSeries]);

  // Reset local state when navigating to a different study (e.g. notification deep-link).
  useEffect(() => {
    setSelectedInstance(null);
    setFilteredFrameCounts({});
    setFailedThumbnailIds(new Set());
  }, [studyUID]);

  // Auto-select instance: prefer initialSopInstanceUID (from thumbnail / notification), else first.
  useEffect(() => {
    if (instances.length === 0) return;

    const selectedInCurrentStudy =
      selectedInstance != null &&
      instances.some((i) => i.sop_instance_uid === selectedInstance.sop_instance_uid);

    if (selectedInCurrentStudy) {
      if (
        initialSopInstanceUID &&
        selectedInstance!.sop_instance_uid !== initialSopInstanceUID
      ) {
        const target = instances.find((i) => i.sop_instance_uid === initialSopInstanceUID);
        if (target) setSelectedInstance(target);
      }
      return;
    }

    if (initialSopInstanceUID) {
      const target = instances.find((i) => i.sop_instance_uid === initialSopInstanceUID);
      setSelectedInstance(target ?? instances[0]);
    } else {
      setSelectedInstance(instances[0]);
    }
  }, [instances, selectedInstance, initialSopInstanceUID]);

  const instanceThumbnailKey = useMemo(
    () =>
      instances
        .map((i) => `${i.sop_instance_uid}|${i.first_frame_image_url || ''}`)
        .sort()
        .join(','),
    [instances]
  );
  useEffect(() => {
    const timer = setTimeout(() => {
      setInstancePanelOpen(false);
    }, 3000);

    // Cleanup the timer if the component unmounts before 2 seconds
    return () => clearTimeout(timer);
  }, []);
  // Fetch thumbnails with credentials (auth cookie) and create blob URLs for <img>
  useEffect(() => {
    const currentIds = new Set(instances.map((i) => i.sop_instance_uid));
    setThumbnailBlobUrls((prev) => {
      const next = { ...prev };
      let revoked = false;
      Object.keys(next).forEach((id) => {
        if (!currentIds.has(id)) {
          URL.revokeObjectURL(next[id]);
          delete next[id];
          revoked = true;
        }
      });
      return revoked ? next : prev;
    });

    const urlsToFetch = instances.filter(
      (i) => i.first_frame_image_url && !failedThumbnailIds.has(i.sop_instance_uid)
    );
    if (urlsToFetch.length === 0) return;

    const controller = new AbortController();
    const created: string[] = [];

    urlsToFetch.forEach((inst) => {
      if (!inst.first_frame_image_url) return;
      apiClient
        .getBlob(inst.first_frame_image_url, { signal: controller.signal })
        .then((res: any) => {
          const blob = res?.data ?? res;
          if (blob && blob instanceof Blob) {
            const blobUrl = URL.createObjectURL(blob);
            created.push(blobUrl);
            setThumbnailBlobUrls((prev) => ({ ...prev, [inst.sop_instance_uid]: blobUrl }));
          }
        })
        .catch(() => {
          setFailedThumbnailIds((prev) => {
            if (prev.has(inst.sop_instance_uid)) return prev; // already failed — avoid new Set reference
            return new Set(prev).add(inst.sop_instance_uid);
          });
        });
    });

    return () => controller.abort();
  }, [instanceThumbnailKey, failedThumbnailIds]);

  const queryClient = useQueryClient();
  // On unmount: revoke thumbnails, clear blob cache, and drop frame metadata from React Query so heap stays under ~2 GB
  useEffect(() => {
    return () => {
      Object.values(thumbnailBlobUrlsRef.current).forEach((url) => url && URL.revokeObjectURL(url));
      clearGeneralViewerFrameCache();
      queryClient.removeQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'general-viewer-instance-frames',
      });
    };
  }, [queryClient]);

  const handleSerieFilterChange = (serieId: string) => {
    setSelectedSerieType((prev) => {
      if (serieId === 'All') return ['All'];
      const next = prev.filter((id) => id !== 'All');
      const idx = next.indexOf(serieId);
      if (idx >= 0) {
        next.splice(idx, 1);
        if (next.length === 0) return ['All'];
      } else {
        next.push(serieId);
      }
      return next;
    });
  };

  const handleClearSeries = () => setSelectedSerieType(['All']);

  /** Stable callback so React.memo on GeneralViewerInstanceDetail skips re-render on unrelated parent updates. */
  const handleBackFromInstance = useCallback(() => setSelectedInstance(null), []);

  const handleFrameCountLoaded = useCallback((sopUID: string, count: number) => {
    setFilteredFrameCounts((prev) => {
      if (prev[sopUID] === count) return prev; // avoid re-render if unchanged
      return { ...prev, [sopUID]: count };
    });
  }, []);

  // Scroll the selected thumbnail into view inside the panel
  useEffect(() => {
    if (selectedThumbRef.current) {
      selectedThumbRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedInstance]);

  if (!studyUID) {
    return (
      <div className="h-full flex items-center justify-center text-slate-500 bg-white dark:bg-gray-950">
        {t('states.noStudySelected')}
      </div>
    );
  }

  if (isErrorInstances) {
    return (
      <div className="h-full flex items-center justify-center text-red-500 bg-white dark:bg-gray-950">
        {t('states.errorLoadingInstances')}
      </div>
    );
  }

  const selectedIdx = selectedInstance
    ? instances.findIndex((i) => i.sop_instance_uid === selectedInstance.sop_instance_uid)
    : -1;

  return (
    <div className="h-full flex flex-col min-h-0">

      {/* ── Top bar ─────────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center gap-2.5 px-4 h-11 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">

        {/* Instances toggle */}
        <button
          type="button"
          onClick={() => setInstancePanelOpen((o) => !o)}
          className={`
    flex items-center gap-1.5 h-7 px-3 rounded-lg text-xs font-semibold transition-all duration-150 shrink-0 border
    ${instancePanelOpen
              ? 'bg-blue-600 text-white border-blue-600 shadow-[0_2px_8px_rgba(37,99,235,0.35)] dark:shadow-[0_4px_12px_rgba(37,99,235,0.2)]'
              : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 hover:text-gray-900 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200'
            }
  `}
        >
          <Layers size={12} />
          <span>{t('topbar.instances')}</span>

          {!isLoadingInstances && instances.length > 0 && (
            <span
              className={`
        ml-0.5 px-1.5 py-0.5 rounded text-[9px] font-black min-w-[18px] text-center transition-colors
        ${instancePanelOpen
                  ? 'bg-white/25 text-white'
                  : 'bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                }
      `}
            >
              {instances.length}
            </span>
          )}
        </button>

        {/* Active instance breadcrumb */}
        {selectedInstance && selectedIdx >= 0 && (
          <>
            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 shrink-0" />
            <div className="flex items-center gap-1.5 min-w-0 px-2 py-1 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 shrink-0">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-[10px] font-semibold text-green-700 dark:text-green-400">
                {t('topbar.instanceIndex', { index: selectedIdx + 1 })}
              </span>
            </div>
          </>
        )}

        <div className="flex-1" />

        <ViewerToolCluster
          studyUID={studyUID}
          variant="light"
          dicomActive={dicomMetadataOpen}
          onDicomClick={() => setDicomMetadataOpen((o) => !o)}
          dicomInstances={instances}
          liveExport={selectedInstance ? liveExport : null}
          examExportMenu
          className="shrink-0"
        />

        {availableSeries.length > 1 && (
          <Select
            className="h-7 text-xs rounded-lg px-2.5 bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700 text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition-all"
            value={selectedSerieType[0] === 'All' ? 'All' : selectedSerieType.join(',')}
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'All') handleClearSeries();
              else handleSerieFilterChange(v);
            }}
            options={[
              { value: 'All', label: t('topbar.allSeries') },
              ...availableSeries.map((s) => ({
                value: s.serie_instance_uid,
                label: s.type || s.serie_instance_uid.slice(-8),
              })),
            ]}
          />
        )}

        <SmartViewerFilter
          filter={smartFilter}
          onChange={handleSmartFilterChange}
          viewOptions={viewOptions}
          structureOptions={structureOptions}
          groupOptions={groupOptions}
          matchedCount={instances.length}
          showContentScope={false}
          buttonLabel={t('topbar.filters')}
          buttonHint={t('smartFilter.buttonHint')}
          matchLabel={t('smartFilter.instancesMatch', { count: instances.length })}
          emptyViewsLabel={t('smartFilter.emptyViewsForGroups')}
          emptyStructuresLabel={t('smartFilter.emptyStructures')}
          emptyGroupsLabel={t('smartFilter.emptyGroups')}
          tip={t('smartFilter.tipStudy')}
        />
      </div>

      {/* ── Viewer area + sliding panel ───────────────────────────────────────── */}
      <div className="flex-1 min-h-0 relative overflow-hidden bg-gray-50 dark:bg-gray-950">

        {/* Main viewer — always full width; instance panel overlays on top */}
        <div
          className="absolute top-0 left-0 right-0 bottom-0"
        >
          {selectedInstance ? (
            <GeneralViewerInstanceDetail
              key={`${studyUID}:${selectedInstance.sop_instance_uid}`}
              studyUID={studyUID}
              instance={selectedInstance}
              filters={filters}
              onBack={handleBackFromInstance}
              onFrameCountLoaded={handleFrameCountLoaded}
              initialPendingViewCode={initialPendingViewCode}
              onViewConfirmedAndSaved={onViewConfirmedAndSaved}
              initialFrameId={selectedInstance.sop_instance_uid === initialSopInstanceUID ? (initialFrameId ?? undefined) : undefined}
              onLiveExportHandlersChange={setLiveExport}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center gap-5 p-8 select-none">
              {isLoadingInstances ? (
                <>
                  <div className="relative w-12 h-12">
                    <div className="absolute inset-0 rounded-full border-[3px] border-blue-100 border-t-blue-500 animate-spin" />
                    <div className="absolute inset-2.5 rounded-full border-2 border-blue-100 border-b-blue-400 animate-spin" style={{ animationDuration: '0.75s', animationDirection: 'reverse' }} />
                  </div>
                  <p className="text-sm text-gray-400">{t('states.loadingInstances')}</p>
                </>
              ) : instances.length === 0 ? (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                    <ImageIcon size={22} className="text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-400">{t('states.noInstancesFound')}</p>
                </>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-2xl bg-white border border-gray-200 shadow-sm flex items-center justify-center">
                    <Layers size={22} className="text-blue-500" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-600">{t('states.selectAnInstance')}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t('states.clickInstancesHint', { instancesLabel: t('topbar.instances') })}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Instance panel ── */}
        <div
          className="absolute top-0 left-0 bottom-0 z-20 flex flex-col transition-transform duration-200 ease-out bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800"
          style={{
            width: 160,
            boxShadow: instancePanelOpen ? '4px 0 24px rgba(0,0,0,0.10), 2px 0 6px rgba(0,0,0,0.06)' : 'none',
            transform: instancePanelOpen ? 'translateX(0)' : 'translateX(-100%)',
          }}
        >
          {/* Panel header */}
          <div className="flex-shrink-0 flex items-center justify-between px-3 h-9 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <div className="w-0.5 h-3.5 rounded-full bg-blue-500" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                {isLoadingInstances ? t('states.loading') : `${instances.length} Instance${instances.length !== 1 ? 's' : ''}`}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setInstancePanelOpen(false)}
              className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <X size={12} />
            </button>
          </div>

          {/* Instance list */}
          <div
            className="flex-1 overflow-y-auto py-2 px-2 space-y-1.5"
            style={{ scrollbarWidth: 'thin' }}
          >
            {isLoadingInstances ? (
              [1, 2, 3].map((n) => (
                <div key={n} className="rounded-xl overflow-hidden animate-pulse bg-gray-100 dark:bg-gray-800">
                  <div className="mx-2 mt-2 aspect-[4/3] rounded-lg bg-gray-200 dark:bg-gray-700" />
                  <div className="px-2.5 py-2 space-y-1.5">
                    <div className="h-2 rounded bg-gray-200 dark:bg-gray-700" style={{ width: '70%' }} />
                    <div className="h-1.5 rounded bg-gray-100 dark:bg-gray-800" style={{ width: '45%' }} />
                  </div>
                </div>
              ))
            ) : instances.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <ImageIcon size={20} className="text-gray-300 dark:text-gray-600" />
                <span className="text-[10px] text-gray-400">{t('states.noneFound')}</span>
              </div>
            ) : (
              instances.map((inst, idx) => {
                const isSelected = selectedInstance?.sop_instance_uid === inst.sop_instance_uid;
                const frameCount =
                  filteredFrameCounts[inst.sop_instance_uid] ??
                  inst.filtered_frame_count ??
                  inst.frame_count;
                const isFiltered = frameCount !== inst.frame_count;

                return (
                  <button
                    key={inst.sop_instance_uid}
                    ref={isSelected ? selectedThumbRef : null}
                    type="button"
                    onClick={() => setSelectedInstance(inst)}
                    className={`w-full text-left rounded-xl overflow-hidden transition-all duration-200 group focus:outline-none border
              ${isSelected
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-500/50 shadow-sm'
                        : 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                      }`}
                    title={t('instanceCard.title', { index: idx + 1, frames: frameCount })}
                  >
                    <div className="mx-2 mt-2 aspect-[4/3] relative rounded-lg overflow-hidden bg-gray-900">
                      {inst.first_frame_image_url && !failedThumbnailIds.has(inst.sop_instance_uid) ? (
                        thumbnailBlobUrls[inst.sop_instance_uid] ? (
                          <img
                            src={thumbnailBlobUrls[inst.sop_instance_uid]}
                            alt=""
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-5 h-5 rounded-full border-2 border-blue-200 border-t-blue-500 animate-spin" />
                          </div>
                        )
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ImageIcon size={16} className="text-gray-600" />
                        </div>
                      )}
                      <span className="absolute top-1 left-1 h-4 min-w-[16px] px-1 rounded text-white text-[9px] font-bold flex items-center justify-center bg-black/60 backdrop-blur-sm">
                        {idx + 1}
                      </span>

                      {!isSelected && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end justify-center pb-1.5"
                          style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)' }}>
                          <span className="text-white text-[9px] font-semibold">{t('states.open')}</span>
                        </div>
                      )}
                    </div>

                    <div className="px-2.5 pt-1.5 pb-2">
                      <p className={`text-[10px] font-semibold truncate ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {isFiltered
                          ? t('instanceCard.framesOfTotal', { count: frameCount, total: inst.frame_count })
                          : t('instanceCard.frames', { count: frameCount })}
                      </p>
                      {isSelected ? (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                          </span>
                          <span className="text-[9px] font-semibold text-green-600 dark:text-green-400">{t('states.viewing')}</span>
                        </div>
                      ) : (
                        <p className="text-[9px] font-mono truncate mt-0.5 text-gray-400 dark:text-gray-500">
                          ···{inst.sop_instance_uid?.slice(-10)}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      <GeneralViewerDicomMetadataPanel
        open={dicomMetadataOpen}
        onClose={() => setDicomMetadataOpen(false)}
        studyUID={studyUID}
        primarySeriesUid={primarySeriesUid}
        selectedInstance={selectedInstance}
      />
    </div>
  );
}
